import { SearchIcon, XIcon } from "@heroicons/react/solid";
import clsx from "clsx";
import { MouseEvent } from "react";

import { TextField } from "@/features/common/components/form/TextField";
import { usePreApplyWhereQuery } from "@/features/common/hooks/usePreApplyWhereQuery";
import { useRequestQuery } from "@/features/common/hooks/useRequestQuery";

export type FilterFieldProps = {
  variant?: "default" | "light";
  label: string;
  name: string;
};

const variantClassName = {
  default: {
    border: "border-gray-400 divide-gray-400",
    container: "rounded-full w-max",
    icon: "text-gray-700",
  },
  light: {
    border: "border-gray-200 divide-gray-100",
    container: "rounded-lg w-full",
    icon: "text-primary",
  },
};

export const SearchBox = <WHERE, ORDER_BY>({
  variant = "default",
  label,
  name,
}: FilterFieldProps) => {
  const { preApplyWhere, setPreApplyWhere } = usePreApplyWhereQuery<WHERE>();
  const { query, setWhere } = useRequestQuery<WHERE, ORDER_BY>();
  const inputField = preApplyWhere?.[name]?.contains;
  const queryParam = query?.where?.[name]?.contains;

  const onClear = (e: MouseEvent) => {
    if (!inputField) {
      e.preventDefault();
      return;
    }
    setWhere({ ...preApplyWhere, [name]: undefined });
  };

  const onSearch = () => {
    setWhere(preApplyWhere);
  };

  const InfoIcon = !!inputField ? XIcon : SearchIcon;

  return (
    <div
      className={clsx(
        "flex items-center divide-x border bg-white px-4 py-2",
        !!queryParam
          ? "border-primary ring-primary divide-primary"
          : variantClassName[variant].border,
        variantClassName[variant].container,
      )}
    >
      <div className="pr-2">
        <InfoIcon
          className={clsx(
            "h-4 w-4",
            !!queryParam ? "text-primary" : variantClassName[variant].icon,
            !!inputField ? "cursor-pointer" : "",
          )}
          onClick={onClear}
        />
      </div>

      <div className="w-full pl-2">
        <TextField
          placeholder={`搜尋${label}`}
          value={inputField ?? ""}
          variant="text"
          inputClassName={clsx(
            !!query?.where?.[name]?.contains && "!text-primary",
            "font-light !py-0 !px-0",
          )}
          onChange={(e) => {
            setPreApplyWhere({
              ...preApplyWhere,
              [name]: { contains: e.target.value, mode: "insensitive" },
            });
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSearch();
          }}
        />
      </div>
    </div>
  );
};
